import { create } from 'zustand';

/**
 * Per-tab admin tenant selection (Phase 51 D-01 / D-02 / D-03).
 *
 * A `platform_admin` may work in a tenant other than its home tenant, but ONLY on `/admin` pages
 * (D-01). The selection defaults to the home tenant (D-02) and lives per browser tab (D-03).
 * Identity (home tenant, role) flows AuthProvider -> this store via `syncIdentity`, never the
 * reverse, so `axios-instance.ts` can import this module without importing AuthProvider.
 *
 * `resolveEffectiveTenant` is the single decision function: the axios request interceptor uses
 * its `headerValue`, React components use it through `useEffectiveTenant`.
 */

export const TENANT_HEADER = 'X-Maja-Tenant';
export const ADMIN_TENANT_STORAGE_KEY = 'maja:admin-tenant:v1';

export interface AdminTenantIdentity {
  /** True once `/bff/user` has answered (not loading anymore). */
  identityKnown: boolean;
  homeTenantId: string | null;
  isPlatformAdmin: boolean;
}

export interface AdminTenantState {
  selectedTenantId: string | null;
  homeTenantId: string | null;
  isPlatformAdmin: boolean;
  /** null or the home tenant id clears the selection. */
  select: (tenantId: string | null) => void;
  clear: () => void;
  syncIdentity: (identity: AdminTenantIdentity) => void;
}

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isGuid(value: string | null | undefined): value is string {
  return typeof value === 'string' && GUID_PATTERN.test(value);
}

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Reads the persisted selection; anything that is not a GUID is dropped (T-51-11). */
function readPersisted(): string | null {
  const storage = getSessionStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(ADMIN_TENANT_STORAGE_KEY);
    if (raw === null) return null;
    if (isGuid(raw)) return raw.toLowerCase();
    storage.removeItem(ADMIN_TENANT_STORAGE_KEY);
  } catch {
    /* storage unusable: fall back to the home tenant */
  }
  return null;
}

function writePersisted(tenantId: string | null): void {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    if (tenantId === null) {
      storage.removeItem(ADMIN_TENANT_STORAGE_KEY);
    } else {
      storage.setItem(ADMIN_TENANT_STORAGE_KEY, tenantId);
    }
  } catch {
    /* quota / blocked storage: the selection keeps working in memory for this session */
  }
}

function sameId(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase();
}

export const useAdminTenantStore = create<AdminTenantState>((set, get) => ({
  selectedTenantId: readPersisted(),
  homeTenantId: null,
  isPlatformAdmin: false,
  select: (tenantId) => {
    if (tenantId === null || sameId(tenantId, get().homeTenantId)) {
      writePersisted(null);
      set({ selectedTenantId: null });
      return;
    }
    const normalized = tenantId.toLowerCase();
    writePersisted(normalized);
    set({ selectedTenantId: normalized });
  },
  clear: () => {
    writePersisted(null);
    set({ selectedTenantId: null });
  },
  syncIdentity: ({ identityKnown, homeTenantId, isPlatformAdmin }) => {
    const current = get().selectedTenantId;
    const dropSelection = (identityKnown && !isPlatformAdmin) || sameId(current, homeTenantId);
    if (dropSelection) writePersisted(null);
    set({
      homeTenantId,
      isPlatformAdmin,
      ...(dropSelection ? { selectedTenantId: null } : {}),
    });
  },
}));

/** True only for `/admin` itself or a path below it — `/administrator` is NOT an admin path. */
export function isAdminPath(pathname: string): boolean {
  return pathname === '/admin' || pathname.startsWith('/admin/');
}

export interface EffectiveTenant {
  effectiveTenantId: string | null;
  isOverridden: boolean;
  headerValue: string | null;
}

/**
 * D-01: the override applies only on /admin pages, only for a platform_admin, and only when the
 * selection differs from the home tenant. Everything else works in the home tenant.
 */
export function resolveEffectiveTenant(input: {
  pathname: string;
  homeTenantId: string | null;
  selectedTenantId: string | null;
  isPlatformAdmin: boolean;
}): EffectiveTenant {
  const { pathname, homeTenantId, selectedTenantId, isPlatformAdmin } = input;
  if (
    isAdminPath(pathname) &&
    isPlatformAdmin &&
    isGuid(selectedTenantId) &&
    !sameId(selectedTenantId, homeTenantId)
  ) {
    return { effectiveTenantId: selectedTenantId, isOverridden: true, headerValue: selectedTenantId };
  }
  return { effectiveTenantId: homeTenantId, isOverridden: false, headerValue: null };
}

/**
 * D-03 / D-08 fallback: the server refused the selected tenant (400 UNKNOWN_TENANT or
 * INVALID_TENANT_HEADER). A 503 TENANT_CHECK_UNAVAILABLE is transient and is NOT a rejection.
 */
export function isTenantHeaderRejection(status: number | undefined, data: unknown): boolean {
  if (status !== 400 || !data || typeof data !== 'object') return false;
  const code = (data as { errorCode?: unknown }).errorCode;
  return code === 'UNKNOWN_TENANT' || code === 'INVALID_TENANT_HEADER';
}
